import type { VaultTenantRoleAssignment } from './vault-active-tenant.util';
import { isSimappeProductCompanyLabel } from './vault-nebula-company.util';
import { normalizeVaultTenantId } from './vault-tenant-scope.util';

const VAULT_TENANT_ASSIGNMENTS_KEY = 'vault_user_tenant_assignments';
const VAULT_AUTH_ME_TENANTS_KEY = 'vault_auth_me_tenants';

export interface VaultTenantAssignmentsRecord {
  email: string;
  vault_user_id?: string;
  assignments: VaultTenantRoleAssignment[];
  updated_at: string;
}

function readUserEmailFromStorage(): string {
  if (typeof localStorage === 'undefined') return '';
  try {
    const raw = localStorage.getItem('user_data');
    if (!raw) return '';
    const data = JSON.parse(raw) as Record<string, unknown>;
    return String(data['email'] ?? data['username'] ?? '').trim().toLowerCase();
  } catch {
    return '';
  }
}

export function readVaultTenantAssignmentsRecord(): VaultTenantAssignmentsRecord | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(VAULT_TENANT_ASSIGNMENTS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as VaultTenantAssignmentsRecord;
    if (!parsed || !Array.isArray(parsed.assignments)) return null;
    const email = readUserEmailFromStorage();
    if (email && parsed.email && parsed.email.toLowerCase() !== email) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function readVaultTenantAssignmentsFromStorage(): VaultTenantRoleAssignment[] {
  return readVaultTenantAssignmentsRecord()?.assignments ?? [];
}

/** Reemplaza asignación tenant+rol en sesión (sin merge con admin/rol anterior). */
export function replaceVaultTenantRoleInSession(
  tenantId: string,
  role: string,
  tenantName?: string,
): void {
  const id = normalizeVaultTenantId(tenantId) || tenantId.trim();
  const trimmedRole = role.trim();
  if (!id || !trimmedRole) return;

  const row: VaultTenantRoleAssignment = {
    tenant_id: id,
    role: trimmedRole,
    ...(tenantName?.trim() ? { tenant_name: tenantName.trim() } : {}),
  };

  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(VAULT_AUTH_ME_TENANTS_KEY, JSON.stringify([row]));
  }
  persistVaultTenantAssignmentsToStorage([row], { replace: true });
}

export function persistVaultTenantAssignmentsToStorage(
  assignments: VaultTenantRoleAssignment[],
  opts?: { vault_user_id?: string; replace?: boolean },
): void {
  if (typeof localStorage === 'undefined') return;
  const email = readUserEmailFromStorage();
  if (!email && !assignments.length) return;

  const prev = readVaultTenantAssignmentsRecord();
  const byKey = new Map<string, VaultTenantRoleAssignment>();
  const push = (rows: VaultTenantRoleAssignment[]) => {
    for (const row of rows) {
      const tenant_id = normalizeVaultTenantId(row.tenant_id) || (row.tenant_id || '').trim();
      const role = (row.role || '').trim();
      const tenant_name = (row.tenant_name ?? '').trim();
      if (!tenant_id && !role && !tenant_name) continue;
      const key = tenant_id || `name:${tenant_name}` || `role:${role}`;
      const prevRow = byKey.get(key);
      byKey.set(key, {
        tenant_id: tenant_id || prevRow?.tenant_id || '',
        tenant_name:
          tenant_name && !isSimappeProductCompanyLabel(tenant_name)
            ? tenant_name
            : prevRow?.tenant_name,
        role: role || prevRow?.role || '',
      });
    }
  };

  if (opts?.replace) {
    push(assignments);
  } else {
    push(prev?.assignments ?? []);
    push(assignments);
  }

  const merged = [...byKey.values()].filter((r) => {
    const id = normalizeVaultTenantId(r.tenant_id);
    const name = (r.tenant_name ?? '').trim();
    return !!id || (!!name && !isSimappeProductCompanyLabel(name));
  });
  if (!merged.length && !prev) return;

  const record: VaultTenantAssignmentsRecord = {
    email: email || prev?.email || '',
    vault_user_id: opts?.vault_user_id || prev?.vault_user_id,
    assignments: merged,
    updated_at: new Date().toISOString(),
  };
  localStorage.setItem(VAULT_TENANT_ASSIGNMENTS_KEY, JSON.stringify(record));
}

export function clearVaultTenantAssignmentsStorage(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(VAULT_TENANT_ASSIGNMENTS_KEY);
}
